import { forwardRef, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { callListSortRuleLabels, callListSortRules, type CallListSortRule } from '@brimo/shared';
import { Banner, Button, Checkbox, Select, TextField } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { queryRoots } from '../../../lib/events';
import { putListSetters, updateList, type EventTypeRef, type ListDetail } from '../api';
import { EventTypeCombobox } from '../EventTypeCombobox';
import { Eyebrow } from '../ui';
import styles from './lists.module.css';

/** „9:00 – 20:00 Uhr" from „09:00"/„20:00". */
function windowLabel(start: string, end: string): string {
  const trim = (time: string) => time.replace(/^0/, '');
  return `${trim(start)} – ${trim(end)} Uhr`;
}

/**
 * „Dialer-Einstellungen" of Terminierung 05–07: the mandatory event type as the search field, the call
 * window, the order and the follow-up rule, the setters. „eingeklappt: 2 weitere" opens the editor of the
 * window, the order rules and the setters.
 */
export const SettingsCard = forwardRef<
  HTMLElement,
  { list: ListDetail; expanded: boolean; onExpandedChange: (open: boolean) => void }
>(function SettingsCard({ list, expanded, onExpandedChange }, ref) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [windowStart, setWindowStart] = useState(list.timeWindowStart);
  const [windowEnd, setWindowEnd] = useState(list.timeWindowEnd);
  const [sortRule, setSortRule] = useState<CallListSortRule>(list.sortRule);
  const [followUpsFirst, setFollowUpsFirst] = useState(list.followUpsFirst);
  const [setters, setSetters] = useState<string[]>(list.setters.map((setter) => setter.id));

  useEffect(() => {
    setWindowStart(list.timeWindowStart);
    setWindowEnd(list.timeWindowEnd);
    setSortRule(list.sortRule);
    setFollowUpsFirst(list.followUpsFirst);
    setSetters(list.setters.map((setter) => setter.id));
  }, [list]);

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: [...queryRoots.dialer] });
  }

  async function changeEventType(eventType: EventTypeRef) {
    setError(null);
    try {
      await updateList(list.id, { eventTypeId: eventType.id });
      await refresh();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }

  async function save() {
    setPending(true);
    setError(null);
    try {
      await updateList(list.id, {
        timeWindowStart: windowStart,
        timeWindowEnd: windowEnd,
        sortRule,
        followUpsFirst,
      });
      const current = list.setters
        .map((setter) => setter.id)
        .sort()
        .join(',');
      if ([...setters].sort().join(',') !== current) await putListSetters(list.id, setters);
      await refresh();
      onExpandedChange(false);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setPending(false);
    }
  }

  return (
    <section ref={ref} className={styles.settingsCard} aria-label="Dialer-Einstellungen" tabIndex={-1}>
      <div className={styles.settingsHead}>
        <Eyebrow>Dialer-Einstellungen</Eyebrow>
        {list.canEdit ? (
          <button
            type="button"
            className={styles.disclosure}
            aria-expanded={expanded}
            aria-controls={`dialer-settings-${list.id}`}
            onClick={() => onExpandedChange(!expanded)}
          >
            {expanded ? 'einklappen' : 'eingeklappt: 2 weitere'}
          </button>
        ) : null}
      </div>
      <div className={styles.settingsBody}>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        <div className={styles.eventTypeBlock}>
          <span className={styles.fieldLabel}>
            Ereignistyp für Termine aus dieser Liste<span className={styles.required}> *</span>
          </span>
          {list.canEdit ? (
            <EventTypeCombobox
              contactId={null}
              value={list.eventType}
              onChange={(value) => void changeEventType(value)}
              label="Ereignistyp für Termine aus dieser Liste"
            />
          ) : (
            <span className={styles.settingValue}>
              {list.eventType ? `${list.eventType.name} · ${list.eventType.durationMinutes} Min.` : '—'}
            </span>
          )}
          <span className={styles.fieldHelp}>
            Sucht in allen Ereignissen aus Beratung, auch selbst angelegten. Dauer, Ort und Closer kommen aus
            dem Ereignis.
          </span>
        </div>
        <div className={styles.settingRows}>
          <div className={styles.settingRow}>
            <span className={styles.settingLabel}>Anrufzeitfenster</span>
            <span className={styles.settingValue}>
              {windowLabel(list.timeWindowStart, list.timeWindowEnd)}
            </span>
          </div>
          <div className={styles.settingRow}>
            <span className={styles.settingLabel}>Reihenfolge</span>
            <span className={styles.settingValue}>{callListSortRuleLabels[list.sortRule]}</span>
          </div>
          <div className={styles.settingRow}>
            <span className={styles.settingLabel}>Wiedervorlagen</span>
            <span className={styles.settingValue}>
              {list.followUpsFirst ? 'haben Vorrang' : 'ohne Vorrang'}
            </span>
          </div>
        </div>
        {expanded && list.canEdit ? (
          <div className={styles.moreSettings} id={`dialer-settings-${list.id}`}>
            <div className={styles.filterEditRow}>
              <TextField
                label="Anrufzeitfenster von"
                type="time"
                size="compact"
                value={windowStart}
                onChange={(event) => setWindowStart(event.target.value)}
              />
              <TextField
                label="bis"
                type="time"
                size="compact"
                value={windowEnd}
                onChange={(event) => setWindowEnd(event.target.value)}
              />
            </div>
            <Select
              label="Reihenfolge"
              size="compact"
              value={sortRule}
              options={callListSortRules.map((rule) => ({
                value: rule,
                label: callListSortRuleLabels[rule],
              }))}
              onChange={(event) => setSortRule(event.target.value as CallListSortRule)}
            />
            <Checkbox
              label="Wiedervorlagen haben Vorrang"
              checked={followUpsFirst}
              onChange={(event) => setFollowUpsFirst(event.target.checked)}
            />
            <fieldset className={styles.dialogFieldset}>
              <legend className={styles.dialogLabel}>Setter auf dieser Liste</legend>
              {list.setterOptions.map((option) => (
                <Checkbox
                  key={option.id}
                  label={option.name}
                  checked={setters.includes(option.id)}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setSetters((current) =>
                      checked ? [...current, option.id] : current.filter((id) => id !== option.id),
                    );
                  }}
                />
              ))}
            </fieldset>
            <div className={styles.saveRow}>
              <Button size="small" loading={pending} onClick={() => void save()}>
                Einstellungen speichern
              </Button>
              <Button variant="secondary" size="small" onClick={() => onExpandedChange(false)}>
                Abbrechen
              </Button>
            </div>
          </div>
        ) : null}
        <div className={styles.settersBlock}>
          <div className={styles.settersLabel}>Setter auf dieser Liste</div>
          <div className={styles.setterChips}>
            {list.setters.length === 0 ? (
              <span className={styles.savedNote}>Noch keine Setter.</span>
            ) : (
              list.setters.map((setter) => (
                <span key={setter.id} className={styles.setterChip}>
                  <span className={styles.setterAvatar} aria-hidden="true">
                    {setter.initials}
                  </span>
                  {setter.name}
                </span>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
});
